import { createApiCall } from './index'

/** VRG-353 — read a product listing URL and return field values for review. */

export interface ImportProductVariantOption {
  name: string
  values: string[]
}

export interface ImportProductDuplicate {
  id: string
  title: string
  sku: string
  /** Edits to a sync-owned product are overwritten by the next provider sync. */
  syncOwned: boolean
  matchedOn: 'sku' | 'product_url'
}

export interface ImportProductFromUrlResponse {
  /** False when nothing readable was found. Still a 200 — open an empty form. */
  extracted: boolean
  source: 'json-ld' | 'shopify-json' | 'opengraph' | null
  productUrl: string

  title?: string
  description?: string
  sku?: string
  price?: string
  compareAtPrice?: string
  currency?: string
  available?: boolean
  variantOptions: ImportProductVariantOption[]

  /** Vera-hosted URLs — the API copies images rather than hotlinking them. */
  images: string[]
  imageWarning?: string
  /** VRG-723 — which source images could not be copied, and why. */
  imageFailures?: Array<{ url: string; reason: string }>

  duplicate?: ImportProductDuplicate
}

export const productImportUrlApi = {
  async importFromUrl(url: string): Promise<ImportProductFromUrlResponse> {
    const response = await createApiCall({
      method: 'POST',
      url: '/products/admin/import-from-url',
      data: { url },
    })

    // The shared interceptor RESOLVES 4xx rather than rejecting, so a failed
    // import would otherwise arrive as a successful mutation carrying an error
    // body. Same guard as productApi.syncFromShopify.
    if (response?.status >= 400) {
      throw new Error(response.data?.message || 'Could not import from that URL.')
    }

    // When the request never got a response at all — the API restarting, a
    // dropped connection — the interceptor returns the SERIALISED ERROR rather
    // than a response, so there is no `status` to compare and the check above
    // passes on `undefined >= 400`. Returning `response.data` there handed the
    // caller `undefined`, and reading `.duplicate` off it threw
    // "Cannot read properties of undefined". Fail loudly and legibly instead.
    if (!response?.data) {
      throw new Error('Could not reach Vera to read that page. Check your connection and try again.')
    }

    return response.data
  },
}
